/**
 * RolesPermissionsPanel.js
 * MEVIA v4.0 - Fase 6: Sistema Multi-Administrador
 * Panel para gestionar roles y permisos de usuarios
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Shield, Users, UserPlus, UserMinus, Check, X, 
  Settings, Eye, FileText, BarChart3, ClipboardList,
  Building, RefreshCw, Search, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

// Role colors and icons
const ROLE_CONFIG = {
  super_admin: { color: 'bg-red-100 text-red-800 border-red-200', icon: Shield },
  company_admin: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Building },
  hr_admin: { color: 'bg-green-100 text-green-800 border-green-200', icon: Users },
  catalog_admin: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Settings },
  plan_admin: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: ClipboardList },
  reports_admin: { color: 'bg-cyan-100 text-cyan-800 border-cyan-200', icon: BarChart3 },
  manager: { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Users },
  employee: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Eye },
  nom035_admin: { color: 'bg-teal-100 text-teal-800 border-teal-200', icon: Shield }
};

const MODULE_ICONS = {
  employees: Users,
  catalogs: Settings,
  organizational: Building,
  assessments: FileText,
  action_plans: ClipboardList,
  reports: BarChart3,
  settings: Settings,
  users: Users,
  subscriptions: Shield,
  my_team: Users
};

export const RolesPermissionsPanel = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState({});
  const [modules, setModules] = useState({});
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedActions, setSelectedActions] = useState([]);
  
  // M-03 §3.1: Scope state for role assignment
  const [selectedScopeType, setSelectedScopeType] = useState('cliente');
  const [selectedScopeId, setSelectedScopeId] = useState('');
  const [razonesSociales, setRazonesSociales] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  const labels = {
    es: {
      title: 'Roles y Permisos',
      subtitle: 'Gestiona los roles y permisos de los usuarios de tu empresa',
      roles: 'Roles',
      permissions: 'Permisos',
      auditLog: 'Registro de Auditoría',
      searchUsers: 'Buscar usuarios...',
      selectUser: 'Selecciona un usuario para ver sus roles y permisos',
      assignRole: 'Asignar Rol',
      removeRole: 'Remover Rol',
      customPermissions: 'Permisos Personalizados',
      addPermission: 'Agregar Permiso',
      currentRoles: 'Roles Actuales',
      currentPermissions: 'Permisos Actuales',
      noRoles: 'Sin roles asignados',
      actions: 'Acciones',
      module: 'Módulo',
      selectRole: 'Seleccionar rol',
      selectModule: 'Seleccionar módulo',
      save: 'Guardar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      roleAssigned: 'Rol asignado exitosamente',
      roleRemoved: 'Rol removido exitosamente',
      permissionSaved: 'Permiso guardado exitosamente',
      scope: 'Alcance',
      scopeCliente: 'Toda la empresa',
      scopeRazonSocial: 'Razón Social',
      scopeCentro: 'Centro de Trabajo',
      selectScope: 'Seleccionar alcance',
      selectEntity: 'Seleccionar entidad'
    },
    en: {
      title: 'Roles & Permissions',
      subtitle: 'Manage user roles and permissions for your company',
      roles: 'Roles',
      permissions: 'Permissions',
      auditLog: 'Audit Log',
      searchUsers: 'Search users...',
      selectUser: 'Select a user to view their roles and permissions',
      assignRole: 'Assign Role',
      removeRole: 'Remove Role',
      customPermissions: 'Custom Permissions',
      addPermission: 'Add Permission',
      currentRoles: 'Current Roles',
      currentPermissions: 'Current Permissions',
      noRoles: 'No roles assigned',
      actions: 'Actions',
      module: 'Module',
      selectRole: 'Select role',
      selectModule: 'Select module',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      roleAssigned: 'Role assigned successfully',
      roleRemoved: 'Role removed successfully',
      permissionSaved: 'Permission saved successfully',
      scope: 'Scope',
      scopeCliente: 'Entire company',
      scopeRazonSocial: 'Legal Entity',
      scopeCentro: 'Work Center',
      selectScope: 'Select scope',
      selectEntity: 'Select entity'
    }
  };
  const t = labels[language] || labels.es;

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rolesRes, modulesRes, defaultPermsRes, employeesRes] = await Promise.all([
          axios.get('/permissions/roles'),
          axios.get('/permissions/modules'),
          axios.get('/permissions/default-permissions'),
          axios.get('/companies/employees/detailed')
        ]);

        if (rolesRes.data.success) setRoles(rolesRes.data.data);
        if (modulesRes.data.success) setModules(modulesRes.data.data);
        if (defaultPermsRes.data.success) setDefaultPermissions(defaultPermsRes.data.data);
        if (employeesRes.data.success && employeesRes.data.data) {
          setEmployees(employeesRes.data.data);
          setFilteredEmployees(employeesRes.data.data);
        }
        
        // M-03: Load razones sociales and sucursales for scope selector
        try {
          const meRes = await axios.get('/auth/me');
          if (meRes.data?.success) {
            const empresaId = meRes.data.data.empresa_id;
            const [rsRes, sucRes] = await Promise.all([
              axios.get(`/organization/razones-sociales/${empresaId}`),
              axios.get(`/organization/sucursales/${empresaId}`)
            ]);
            if (rsRes.data?.success) setRazonesSociales(rsRes.data.data || []);
            if (sucRes.data?.success) setSucursales(sucRes.data.data || []);
          }
        } catch (e) {
          logger.error('Error loading scope data:', e);
        }
      } catch (error) {
        logger.error('Error fetching data:', error);
        toast.error('Error loading data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter employees by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredEmployees(employees);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredEmployees(
        employees.filter(e => 
          e.nombre?.toLowerCase().includes(term) || 
          e.email?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, employees]);

  // Fetch user roles and permissions when user is selected
  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`/permissions/users/${userId}/roles`),
        axios.get(`/permissions/users/${userId}/permissions`)
      ]);

      if (rolesRes.data.success) {
        setUserRoles(rolesRes.data.data);
      }
      if (permsRes.data.success) {
        setUserPermissions(permsRes.data.data);
      }
    } catch (error) {
      logger.error('Error fetching user permissions:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserPermissions(selectedUser.id);
    }
  }, [selectedUser, fetchUserPermissions]);

  // Handle role assignment (M-03: with scope)
  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) return;
    if (selectedScopeType !== 'cliente' && !selectedScopeId) {
      toast.error(language === 'es' ? 'Selecciona la entidad de alcance' : 'Select scope entity');
      return;
    }

    try {
      const params = new URLSearchParams({ role: selectedRole, scope_type: selectedScopeType });
      if (selectedScopeId) params.append('scope_id', selectedScopeId);
      
      await axios.post(`/permissions/users/${selectedUser.id}/roles?${params.toString()}`);
      toast.success(t.roleAssigned);
      fetchUserPermissions(selectedUser.id);
      setShowRoleModal(false);
      setSelectedRole('');
      setSelectedScopeType('cliente');
      setSelectedScopeId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // Handle role removal (M-03: scope-aware)
  const handleRemoveRole = async (roleData) => {
    if (!selectedUser) return;
    const role = typeof roleData === 'string' ? roleData : roleData.role;
    const scopeType = typeof roleData === 'string' ? undefined : roleData.scope_type;
    const scopeId = typeof roleData === 'string' ? undefined : roleData.scope_id;

    try {
      const params = new URLSearchParams();
      if (scopeType) params.append('scope_type', scopeType);
      if (scopeId) params.append('scope_id', scopeId);
      const qs = params.toString() ? `?${params.toString()}` : '';
      
      await axios.delete(`/permissions/users/${selectedUser.id}/roles/${role}${qs}`);
      toast.success(t.roleRemoved);
      fetchUserPermissions(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // Handle custom permission
  const handleSaveCustomPermission = async () => {
    if (!selectedUser || !selectedModule || selectedActions.length === 0) return;

    try {
      await axios.post(`/permissions/users/${selectedUser.id}/permissions/custom`, {
        user_id: selectedUser.id,
        module: selectedModule,
        actions: selectedActions
      });
      toast.success(t.permissionSaved);
      fetchUserPermissions(selectedUser.id);
      setShowPermissionModal(false);
      setSelectedModule('');
      setSelectedActions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-500">Cargando...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            {t.title}
          </CardTitle>
          <CardDescription>{t.subtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User List */}
            <div className="lg:col-span-1 border-r pr-4">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t.searchUsers}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    onClick={() => setSelectedUser(employee)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === employee.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        {employee.nombre?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{employee.nombre}</p>
                        <p className="text-sm text-gray-500 truncate">{employee.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Details */}
            <div className="lg:col-span-2">
              {selectedUser ? (
                <div className="space-y-6">
                  {/* User Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-600">
                        {selectedUser.nombre?.charAt(0) || '?'}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold">{selectedUser.nombre}</h3>
                        <p className="text-gray-500">{selectedUser.email}</p>
                      </div>
                    </div>
                  </div>

                  <Tabs defaultValue="roles" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="roles">{t.roles}</TabsTrigger>
                      <TabsTrigger value="permissions">{t.permissions}</TabsTrigger>
                    </TabsList>

                    {/* Roles Tab */}
                    <TabsContent value="roles" className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{t.currentRoles}</h4>
                        <Button size="sm" onClick={() => setShowRoleModal(true)}>
                          <UserPlus className="h-4 w-4 mr-1" />
                          {t.assignRole}
                        </Button>
                      </div>
                      
                      {userRoles.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">{t.noRoles}</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {userRoles.map((roleData, idx) => {
                            const config = ROLE_CONFIG[roleData.role] || ROLE_CONFIG.employee;
                            const Icon = config.icon;
                            const scopeLabel = roleData.scope_type === 'cliente'
                              ? (language === 'es' ? 'Toda la empresa' : 'Entire company')
                              : roleData.scope_name || roleData.scope_id || '';
                            return (
                              <div
                                key={`${roleData.role}-${roleData.scope_type}-${roleData.scope_id || idx}`}
                                className={`p-4 rounded-lg border ${config.color}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">
                                      {language === 'es' ? roleData.name_es : roleData.name_en}
                                    </span>
                                  </div>
                                  {roleData.source !== 'usuarios.rol' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveRole(roleData)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                                <p className="text-sm mt-1 opacity-80">
                                  {language === 'es' ? roleData.description_es : roleData.description_en}
                                </p>
                                {/* M-03: Show scope */}
                                <div className="mt-2 flex items-center gap-1.5">
                                  <Building className="h-3.5 w-3.5 opacity-60" />
                                  <span className="text-xs font-medium opacity-70">
                                    {t.scope}: {scopeLabel}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>

                    {/* Permissions Tab */}
                    <TabsContent value="permissions" className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{t.currentPermissions}</h4>
                        <Button size="sm" onClick={() => setShowPermissionModal(true)}>
                          <Shield className="h-4 w-4 mr-1" />
                          {t.addPermission}
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        {Object.entries(userPermissions).map(([module, actions]) => {
                          const ModuleIcon = MODULE_ICONS[module] || Settings;
                          const moduleInfo = modules[module] || {};
                          return (
                            <div
                              key={module}
                              className="p-4 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <ModuleIcon className="h-4 w-4 text-gray-600" />
                                <span className="font-medium">
                                  {language === 'es' ? moduleInfo.name_es : moduleInfo.name_en || module}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {actions.map((action) => (
                                  <Badge key={action} variant="secondary">
                                    {action}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-16 text-gray-500">
                  <Users className="h-12 w-12 mb-4 opacity-50" />
                  <p>{t.selectUser}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assign Role Modal (M-03: with scope selector) */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.assignRole}</DialogTitle>
            <DialogDescription>
              {selectedUser?.nombre}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Role selector */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.roles}</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectRole} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roles).map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      {language === 'es' ? role.name_es : role.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* M-03 §3.1: Scope type selector */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">{t.scope}</label>
              <Select 
                value={selectedScopeType} 
                onValueChange={(v) => { setSelectedScopeType(v); setSelectedScopeId(''); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectScope} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cliente">{t.scopeCliente}</SelectItem>
                  <SelectItem value="razon_social">{t.scopeRazonSocial}</SelectItem>
                  <SelectItem value="centro">{t.scopeCentro}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Scope entity selector (only when not 'cliente') */}
            {selectedScopeType === 'razon_social' && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.scopeRazonSocial}</label>
                <Select value={selectedScopeId} onValueChange={setSelectedScopeId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectEntity} />
                  </SelectTrigger>
                  <SelectContent>
                    {razonesSociales.map(rs => (
                      <SelectItem key={rs.id} value={rs.id}>
                        {rs.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedScopeType === 'centro' && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">{t.scopeCentro}</label>
                <Select value={selectedScopeId} onValueChange={setSelectedScopeId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectEntity} />
                  </SelectTrigger>
                  <SelectContent>
                    {sucursales.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nombre} {s.codigo ? `(${s.codigo})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRoleModal(false); setSelectedScopeType('cliente'); setSelectedScopeId(''); }}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleAssignRole} 
              disabled={!selectedRole || (selectedScopeType !== 'cliente' && !selectedScopeId)}
            >
              {t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Permission Modal */}
      <Dialog open={showPermissionModal} onOpenChange={setShowPermissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.customPermissions}</DialogTitle>
            <DialogDescription>
              {selectedUser?.nombre}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t.module}</label>
              <Select 
                value={selectedModule} 
                onValueChange={(v) => {
                  setSelectedModule(v);
                  setSelectedActions([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectModule} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(modules).map(([key, module]) => (
                    <SelectItem key={key} value={key}>
                      {language === 'es' ? module.name_es : module.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedModule && modules[selectedModule] && (
              <div>
                <label className="text-sm font-medium mb-2 block">{t.actions}</label>
                <div className="grid grid-cols-2 gap-2">
                  {modules[selectedModule].actions.map((action) => (
                    <label key={action} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedActions.includes(action)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedActions([...selectedActions, action]);
                          } else {
                            setSelectedActions(selectedActions.filter(a => a !== action));
                          }
                        }}
                      />
                      <span className="text-sm">{action}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleSaveCustomPermission} 
              disabled={!selectedModule || selectedActions.length === 0}
            >
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsPanel;
